import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { ILeaderboardEntry } from '@/interfaces';
import { useState } from 'react';
import { ChevronDownIcon } from '../InsightsIcons';
import LogoTile from '../LogoTile';
import { GridSectionTitle, InsightsContainer, InsightsSection } from '../TrackingInsights.styles';
import {
  CARD_LOGO_SIZE,
  Card,
  CardGrid,
  CardName,
  Chevron,
  MOBILE_VISIBLE_CARDS,
  ViewMoreButton,
} from './Leaderboard.styles';

export interface LeaderboardProps {
  /** Already translated and interpolated by the caller, e.g. "Top 10 Carriers in Germany". */
  title: string;
  entries: ILeaderboardEntry[];
  /** Alternates the section background so neighbouring sections stay distinguishable. */
  tinted?: boolean;
}

/**
 * Top carriers or merchants for one tracking page.
 *
 * Rendered as the design draws it: a grid of logo-and-name cards, five across on desktop and one
 * across on mobile. Rank is carried by reading order alone — the design gives every card the same
 * treatment, with no numeral, no top-three emphasis and no volume figure, so none is invented here.
 * The order the entries arrive in is the rank; Growth ranks them in the sheet.
 */
const Leaderboard: React.FC<LeaderboardProps> = ({ title, entries, tinted }) => {
  const { t } = useGlobalState();
  const [isExpanded, setIsExpanded] = useState(false);

  // A section with nothing to show is not an empty state, it simply is not on the page.
  if (!entries.length) return null;

  // A short list renders short. Padding it out with blank cards would imply the missing
  // entries exist, and a country with three carriers genuinely has three.
  const canFold = entries.length > MOBILE_VISIBLE_CARDS;

  return (
    <InsightsSection $tinted={tinted}>
      <InsightsContainer $wide>
        <GridSectionTitle type='h2'>{title}</GridSectionTitle>

        <CardGrid>
          {entries.map((entry, index) => (
            <Card
              key={`${entry.name}-${index}`}
              $foldedOnMobile={canFold && !isExpanded && index >= MOBILE_VISIBLE_CARDS}
            >
              <LogoTile name={entry.name} logoUrl={entry.logoUrl} size={CARD_LOGO_SIZE} />
              <CardName>{entry.name}</CardName>
            </Card>
          ))}
        </CardGrid>

        {canFold && (
          <ViewMoreButton type='button' aria-expanded={isExpanded} onClick={() => setIsExpanded((open) => !open)}>
            {t(isExpanded ? MasterTranslation.ViewLess : MasterTranslation.ViewMore)}
            <Chevron $isOpen={isExpanded}>
              <ChevronDownIcon />
            </Chevron>
          </ViewMoreButton>
        )}
      </InsightsContainer>
    </InsightsSection>
  );
};

export default Leaderboard;
